/**
 * ヘッダのビュー名から他のビューへ切り替える（docs/01-requirements.md §5.17）。
 *
 * ビューは「同じデータに対する見方」であって、ノートの種類ではない（§4.4）。
 * **ラベルに「投稿」のような種類を思わせる語を使わない**（§9 チェックリスト8）。
 *
 * ビューを畳むとき（ADR-030）にここで消すのは `VIEWS` の要素 1 つだけで済むようにしてある。
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@app/components/icons';
import { saveViewName, VIEW_LABEL, viewPath, type ViewName } from '@app/views/view-preference';

/** 切り替え先として出すビュー。畳むときはこの配列から 1 要素消す。 */
const VIEWS: ViewName[] = ['timeline', 'list'];

export interface ViewSwitcherProps {
  current: ViewName;
}

export function ViewSwitcher({ current }: ViewSwitcherProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // `<h1>` の中に置くので phrasing content にする（div は内容モデル上いけない）。
  const rootRef = useRef<HTMLSpanElement | null>(null);

  // 外側のタップと Escape で閉じる。開いている間だけ購読し、
  // 閉じたらクリーンアップで必ず外す（ドキュメントに副作用を残さない）。
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Node && rootRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const select = useCallback(
    (view: ViewName) => {
      setOpen(false);
      // 記憶してから遷移する。次回の起動時はここで選んだビューが開く（§5.1）。
      saveViewName(view);
      if (view !== current) navigate(viewPath(view));
    },
    [current, navigate],
  );

  return (
    <span className="view-switcher" ref={rootRef}>
      <button
        type="button"
        className="view-switcher__button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        {VIEW_LABEL[current]}
        <span className="view-switcher__caret">
          <Icon name="chevron-down" size={16} />
        </span>
      </button>

      {/* `<h1>` の中なので、メニューも phrasing content で組む（`<ul>`/`<li>` は使えない）。
          一覧としての意味づけは role で与える。 */}
      {open && (
        <span className="view-switcher__menu" role="menu" aria-label="ビューを切り替える">
          {VIEWS.map((view) => (
            <button
              key={view}
              type="button"
              role="menuitem"
              className="view-switcher__item"
              {...(view === current ? { 'aria-current': true } : {})}
              onClick={() => select(view)}
            >
              {VIEW_LABEL[view]}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}
